import type { TableColumns } from '@ohpen/enso-ui';
import { FeeDefinition, FeeBand, FieldType, FeeProfile, StringPair } from '@pmp/api';

export type FeesExport = {
  feeId?: number;
  calculationType?: StringPair | null;
  feeType?: StringPair | null;
  feeName?: string | null;
  amount?: number | null;
  percentage?: number | null;
  bandParameter?: StringPair | null;
  bandIndex?: number;
  bandBaseTopAmount?: number | null;
  bandBaseBottomAmount?: number | null;
  bandAmount?: number | null;
};

export const feesColumns: TableColumns<FeeDefinition> = [
  {
    title: 'pmp.fees.labels.feeId',
    dataIndex: 'feeId',
    key: 'feeId',
    options: {
      filter: 'search',
      sorter: true,
      hide: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeName',
    dataIndex: 'feeName',
    key: 'feeName',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 250,
  },
  {
    title: 'pmp.fees.labels.description',
    dataIndex: 'description',
    key: 'description',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 210,
  },
  {
    title: 'pmp.fees.labels.feeTypeId',
    dataIndex: 'feeType',
    key: 'feeTypeId',
    options: {
      format: {
        type: 'valueListKey',
      },
      filter: 'select',
      listName: FieldType.FEE_TYPE,
      hide: true,
    },
    width: 100,
  },
  {
    title: 'pmp.fees.labels.feeType',
    dataIndex: 'feeType',
    key: 'feeType',
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.FEE_TYPE,
    },
    width: 130,
  },
  {
    title: 'pmp.fees.labels.calculationTypeId',
    dataIndex: 'calculationType',
    key: 'calculationTypeId',
    options: {
      format: {
        type: 'valueListKey',
      },
      filter: 'select',
      listName: FieldType.CALCULATION_TYPE,
      hide: true,
    },
    width: 100,
  },
  {
    title: 'pmp.fees.labels.calculationType',
    dataIndex: 'calculationType',
    key: 'calculationType',
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.CALCULATION_TYPE,
    },
    width: 160,
  },
  {
    title: 'pmp.fees.labels.amount',
    dataIndex: 'amount',
    key: 'amount',
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
        decimalScale: 0,
      },
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.percentage',
    dataIndex: 'percentage',
    key: 'percentage',
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'percentage',
      },
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.basedOnId',
    dataIndex: 'basedOn',
    key: 'basedOnId',
    options: {
      format: {
        type: 'valueListKey',
      },
      filter: 'select',
      listName: FieldType.PRODUCT_FEE_BASED_ON,
      hide: true,
      hideInForm: true,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.basedOn',
    dataIndex: 'basedOn',
    key: 'basedOn',
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.PRODUCT_FEE_BASED_ON,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.paymentMethodIds',
    dataIndex: 'paymentMethods',
    key: 'paymentMethodIds',
    options: {
      format: {
        type: 'multiValueListKey',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_METHOD,
      hide: true,
      hideInForm: true,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.paymentMethods',
    dataIndex: 'paymentMethods',
    key: 'paymentMethods',
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_METHOD,
    },
    width: 370,
  },
  {
    title: 'pmp.fees.labels.paymentStageIds',
    dataIndex: 'paymentStages',
    key: 'paymentStageIds',
    options: {
      format: {
        type: 'multiValueListKey',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_STAGE,
      hide: true,
      hideInForm: true,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.paymentStages',
    dataIndex: 'paymentStages',
    key: 'paymentStages',
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_STAGE,
    },
    width: 180,
  },
  {
    title: 'pmp.fees.labels.accountCode',
    dataIndex: 'accountCode',
    key: 'accountCode',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 120,
  },
  {
    title: 'pmp.fees.labels.costCentre',
    dataIndex: 'costCentre',
    key: 'costCentre',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 100,
  },
  {
    title: 'pmp.fees.labels.refundable',
    dataIndex: 'refundable',
    key: 'refundable',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.waivable',
    dataIndex: 'waivable',
    key: 'waivable',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.estimated',
    dataIndex: 'estimated',
    key: 'estimated',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
    width: 150,
  },
  { title: 'enso.labels.actions', dataIndex: '', key: 'actions', width: 100, fixed: 'right' },
];

export const bands: TableColumns<FeeBand> = [
  {
    title: 'pmp.fees.labels.baseBottomAmount',
    dataIndex: 'baseBottomAmount',
    key: 'baseBottomAmount',
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
        decimalScale: 0,
      },
    },
  },
  {
    title: 'pmp.fees.labels.baseTopAmount',
    dataIndex: 'baseTopAmount',
    key: 'baseTopAmount',
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
        decimalScale: 0,
      },
    },
  },
  {
    title: 'pmp.fees.labels.amount',
    dataIndex: 'amount',
    key: 'amount',
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
        decimalScale: 0,
      },
    },
  },
];

export const feesFlatColumns: TableColumns<FeeDefinition> = [
  {
    title: 'pmp.fees.labels.feeId',
    dataIndex: 'feeId',
    key: 'feeId',
  },
  {
    title: 'pmp.fees.labels.feeName',
    dataIndex: 'feeName',
    key: 'feeName',
  },
  {
    title: 'pmp.fees.labels.description',
    dataIndex: 'description',
    key: 'description',
  },
  {
    title: 'pmp.fees.labels.feeType',
    dataIndex: 'feeType',
    key: 'feeType',
    options: {
      format: {
        type: 'valueList',
      },
      listName: FieldType.FEE_TYPE,
    },
  },
  {
    title: 'pmp.fees.labels.calculationType',
    dataIndex: 'calculationType',
    key: 'calculationType',
    options: {
      format: {
        type: 'valueList',
      },
      listName: FieldType.CALCULATION_TYPE,
    },
  },
  {
    title: 'pmp.fees.labels.amount',
    dataIndex: 'amount',
    key: 'amount',
    options: {
      format: {
        type: 'number',
      },
    },
  },
  {
    title: 'pmp.fees.labels.percentage',
    dataIndex: 'percentage',
    key: 'percentage',
    options: {
      format: {
        type: 'number',
      },
    },
  },
  {
    title: 'pmp.fees.labels.basedOn',
    dataIndex: 'basedOn',
    key: 'basedOn',
    options: {
      format: {
        type: 'valueList',
      },
      listName: FieldType.PRODUCT_FEE_BASED_ON,
    },
  },
  {
    title: 'pmp.fees.labels.baseBottomAmount',
    dataIndex: 'baseBottomAmount',
    key: 'baseBottomAmount',
    options: {
      format: {
        type: 'number',
      },
    },
  },
  {
    title: 'pmp.fees.labels.baseTopAmount',
    dataIndex: 'baseTopAmount',
    key: 'baseTopAmount',
    options: {
      format: {
        type: 'number',
      },
    },
  },
  {
    title: 'pmp.fees.labels.paymentMethods',
    dataIndex: 'paymentMethods',
    key: 'paymentMethods',
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_METHOD,
    },
  },
  {
    title: 'pmp.fees.labels.paymentStages',
    dataIndex: 'paymentStages',
    key: 'paymentStages',
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_STAGE,
    },
  },
  {
    title: 'pmp.fees.labels.accountCode',
    dataIndex: 'accountCode',
    key: 'accountCode',
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.costCentre',
    dataIndex: 'costCentre',
    key: 'costCentre',
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.refundable',
    dataIndex: 'refundable',
    key: 'refundable',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'pmp.fees.labels.waivable',
    dataIndex: 'waivable',
    key: 'waivable',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'pmp.fees.labels.estimated',
    dataIndex: 'estimated',
    key: 'estimated',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
];

export const feeProfilesColumns: TableColumns<FeeProfile> = [
  {
    title: 'pmp.fees.labels.feeProfileId',
    dataIndex: 'feeProfileId',
    key: 'feeProfileId',
    options: {
      filter: 'search',
      sorter: true,
      hide: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeProfileName',
    dataIndex: 'feeProfileName',
    key: 'feeProfileName',
    width: 340,
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeIds',
    dataIndex: 'feeIds',
    key: 'feeIds',
    options: {
      filter: 'select',
      format: {
        type: 'multiValueListKey',
      },
      listName: FieldType.FEES,
      hide: true,
      hideInForm: true,
    },
  },
  {
    title: 'pmp.fees.labels.fees',
    dataIndex: 'feeIds',
    key: 'fees',
    options: {
      filter: 'select',
      format: {
        type: 'multiValueList',
      },
      listName: FieldType.FEES,
    },
  },
  { title: 'enso.labels.actions', dataIndex: '', key: 'actions', width: 100, fixed: 'right' },
];

export const feeProfilesExportColumns: TableColumns<FeeProfile> = [
  {
    title: 'pmp.fees.labels.feeProfileId',
    dataIndex: 'feeProfileId',
    key: 'feeProfileId',
    options: {
      filter: 'search',
      sorter: true,
      hide: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeProfileName',
    dataIndex: 'feeProfileName',
    key: 'feeProfileName',
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeIds',
    dataIndex: 'feeIds',
    key: 'feeIds',
    options: {
      filter: 'select',
      format: {
        type: 'multiValueListKey',
      },
      listName: FieldType.FEES,
      hide: true,
      hideInForm: true,
    },
  },
  {
    title: 'pmp.fees.labels.fees',
    dataIndex: 'feeIds',
    key: 'fees',
    options: {
      format: {
        type: 'multiValueList',
      },
      listName: FieldType.FEES,
    },
  },
];
